import { useEffect, useState } from 'react';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import { formatDateUTC } from '../utils/formatDate';

import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Button from '../Components/Buttons';
import { ArtifactApiProvider, useArtifactApi } from '../Services/ArtifactApiProvider';
import { removeExtension } from '../utils/removeFileExtension';

function ArtifactHub({ canEdit }) {
  const { getArtifact, deleteArtifact } = useArtifactApi();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [totalArtifacts, setTotalArtifacts] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedTypes, setSelectedTypes] = useState(['template', 'default']);
  const navigate = useNavigate();
  const location = useLocation();
  const [alertShown, setAlertShown] = useState(false);

  const columns = [
    { name: 'ID', selector: (row) => row.id, sortable: true },
    { name: 'Type', selector: (row) => row.type, sortable: true },
    { name: 'Name', selector: (row) => removeExtension(row.name), sortable: true },
    { name: 'Status', selector: (row) => (row.is_active ? 'Active' : 'Inactive'), sortable: true },
    { name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: true },
    { name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: true },
  ];

  const searchOptions = [
    { label: 'template', value: 'template' },
    { label: 'default', value: 'default' },
    { label: 'document', value: 'document' },
  ];

  useEffect(() => {
    const alerts = location.state?.alerts;

    if (alerts?.length && !alertShown) {
      alerts.forEach((alert) => showAlert(alert.type, alert.message));

      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, showAlert, alertShown]);

  const fetchArtifacts = async (skip, limit, type = selectedTypes) => {
    if (limit !== itemsPerPage) {
      setItemsPerPage(limit);
    }
    try {
      setLoading(true);
      const response = await getArtifact(skip, limit, type);
      setArtifacts(response.items);
      setTotalArtifacts(response.totalCount);
      setItemsPerPage(limit);
    } catch (error) {
      console.error('Error fetching artifact:', error);
      setError('Failed to load artifacts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArtifacts(0, itemsPerPage, selectedTypes);
  }, []);

  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/artifacts/${row.id}`);
  };

  const handleDelete = async (selectedIds) => {
    if (!selectedIds || selectedIds.length === 0) return;
    const count = selectedIds.length;
    showConfirmation(`Are you sure you want to delete ${count} Artifacts?`, async () => {
      try {
        setLoading(true);
        for (const selectedId of selectedIds) {
          await deleteArtifact(selectedId);
        }

        showAlert('success', `${count} artifact${count > 1 ? 's' : ''} successfully deleted!`);
        await fetchArtifacts(0, itemsPerPage, selectedTypes);
      } catch (error) {
        showAlert('fail', `Failed to delete ${count} artifact${count > 1 ? 's' : ''}!`);
      } finally {
        setLoading(false);
      }
    });
  };

  const handleTypeChange = async (skip, limit, newTypes) => {
    setSelectedTypes(newTypes);
    await fetchArtifacts(skip, limit, newTypes);
  };

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title="Artifact"
                columns={columns}
                data={artifacts}
                selectableRowsHighlight
                selectableRows
                progressPending={loading}
                paginationPerPage={itemsPerPage}
                onRowClicked={handleRowClicked}
                onDelete={handleDelete}
                paginationTotalRows={totalArtifacts}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onChangePage={fetchArtifacts}
                onChangeFilter={handleTypeChange}
                typeSearch="multiselect"
                singleSelect={false}
                searchOptions={searchOptions}
                defaultValue={selectedTypes}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() =>
                    navigate(`/organizations/${organizationId}/artifacts/new-artifact`)
                  }
                >
                  Create New artifact
                </Button>
              )}
              <Button
                variant="ghost"
                size="medium"
                onClick={() => navigate(`/organizations/${organizationId}/artifacts/batch-create`)}
              >
                Batch create
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ArtifactsWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <ArtifactApiProvider>
        <ArtifactHub canEdit={canEdit} />
      </ArtifactApiProvider>
    </ConfirmationProvider>
  );
}

export default ArtifactsWrapper;
